import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './components/Sidebar';
import SnippetCard from './components/SnippetCard';
import SnippetDetail from './components/SnippetDetail';
import SnippetForm from './components/SnippetForm';
import { publicDataFetch } from './api';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiLoader } = FiIcons;

// Fixed ID for the user_id column in the database (required by schema)
const SHIFT_ADMIN_ID = 'shift_creative_admin';

function App() {
  const [snippets, setSnippets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'detail', 'add', 'edit'
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState(null);

  useEffect(() => {
    fetchSnippets();
  }, []);

  const fetchSnippets = async () => {
    setIsLoading(true);
    // Using publicDataFetch with the new public_readwrite policy (no owner_id needed in URL)
    const res = await publicDataFetch('read/snippets');
    if (res?.status === 'success') {
      setSnippets(res.data ?? []);
    } else {
      console.error('Fetch failed:', res?.error);
    }
    setIsLoading(false);
  };

  const handleSaveSnippet = async (snippetData) => {
    const isEdit = view === 'edit';
    
    // Mapping fields to match the DB schema exactly
    // Ensure all required fields are present and correctly formatted
    const payload = {
      title: snippetData.title || 'Untitled Snippet',
      description: snippetData.description || '',
      code: snippetData.code || '',
      language: (snippetData.language || 'javascript').toLowerCase(),
      framework: snippetData.framework || '',
      user_id: SHIFT_ADMIN_ID, // Required column in DB
      tags: Array.isArray(snippetData.tags) ? snippetData.tags.join(',') : (snippetData.tags || ''),
      additional_instructions: snippetData.additional_instructions || ''
    };

    const endpoint = isEdit ? `update/snippets/${snippetData.id}` : 'create/snippets';
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const res = await publicDataFetch(endpoint, {
        method,
        body: JSON.stringify(payload)
      });

      if (res?.status === 'success') {
        await fetchSnippets();
        if (isEdit) {
          // Update selected snippet locally to reflect changes in detail view
          const updated = { ...payload, id: snippetData.id };
          // Convert tags back to array for the UI
          updated.tags = updated.tags.split(',').filter(Boolean);
          setSelectedSnippet(updated);
          setView('detail');
        } else {
          setView('list');
        }
      } else {
        const errorMsg = res?.error || 'Unknown error';
        console.error('Save failed:', errorMsg);
        alert(`Error saving snippet: ${errorMsg}. Please ensure all fields are filled.`);
      }
    } catch (err) {
      console.error('Save exception:', err);
      alert('Network error while saving snippet.');
    }
  };

  const handleDeleteSnippet = async (id) => {
    if (!window.confirm('Are you sure you want to delete this snippet?')) return;
    
    const res = await publicDataFetch(`delete/snippets/${id}`, {
      method: 'DELETE'
    });
    
    if (res?.status === 'success') {
      await fetchSnippets();
      setView('list');
      setSelectedSnippet(null);
    } else {
      console.error('Delete failed:', res?.error);
      alert(`Failed to delete snippet: ${res?.error || 'Connection error'}.`);
    }
  };

  // Transformation logic to handle comma-separated tags from DB
  const processedSnippets = useMemo(() => {
    return (snippets || []).map(s => ({
      ...s,
      tags: typeof s.tags === 'string' ? s.tags.split(',').filter(Boolean) : (s.tags || [])
    }));
  }, [snippets]);

  const filteredSnippets = useMemo(() => {
    return processedSnippets.filter(snippet => {
      const matchesSearch = 
        (snippet.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (snippet.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (snippet.tags || []).some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || (snippet.language || '').toLowerCase() === selectedCategory.toLowerCase();
      const matchesTag = !selectedTag || (snippet.tags || []).includes(selectedTag);

      return matchesSearch && matchesCategory && matchesTag;
    });
  }, [processedSnippets, searchQuery, selectedCategory, selectedTag]);

  const uniqueLanguages = useMemo(() => {
    return [...new Set(processedSnippets.map(s => (s.language || '').toLowerCase()))].filter(Boolean).sort();
  }, [processedSnippets]);

  const uniqueTags = useMemo(() => {
    const tags = processedSnippets.flatMap(s => s.tags);
    return [...new Set(tags)].sort();
  }, [processedSnippets]);

  return (
    <div className="flex h-screen bg-slate-950 font-sans text-slate-200 overflow-hidden">
      <Sidebar 
        onAddClick={() => {
          setSelectedSnippet(null);
          setView('add');
        }} 
        categories={uniqueLanguages}
        tags={uniqueTags}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
        selectedTag={selectedTag}
        onSelectTag={setSelectedTag}
      />
      
      <main className="flex-1 flex flex-col min-w-0 h-full relative">
        <header className="bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-10">
          <div className="flex-1 max-w-2xl mx-auto relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon={FiSearch} className="h-4 w-4 text-slate-400 group-focus-within:text-indigo-400 transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search snippets by title, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 text-slate-100 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-indigo-500 transition-all shadow-sm placeholder:text-slate-500"
            />
          </div>
        </header>

        <div className="flex-1 overflow-hidden flex flex-col">
          {view === 'list' && (
            <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar">
              <div className="max-w-7xl mx-auto">
                {isLoading ? (
                  <div className="flex justify-center py-20">
                    <SafeIcon icon={FiLoader} className="w-8 h-8 text-indigo-500 animate-spin" />
                  </div>
                ) : filteredSnippets.length === 0 ? (
                  <div className="text-center py-20 flex flex-col items-center">
                    <div className="bg-slate-900 p-4 rounded-full mb-4">
                      <SafeIcon icon={FiSearch} className="w-8 h-8 text-slate-700" />
                    </div>
                    <p className="text-slate-400 text-lg">No snippets found.</p>
                    <button 
                      onClick={() => setView('add')}
                      className="mt-4 text-indigo-400 hover:text-indigo-300 text-sm font-medium"
                    >
                      Create your first snippet
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredSnippets.map(snippet => (
                      <SnippetCard 
                        key={snippet.id} 
                        snippet={snippet} 
                        onClick={() => {
                          setSelectedSnippet(snippet);
                          setView('detail');
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {view === 'detail' && selectedSnippet && (
            <SnippetDetail 
              snippet={selectedSnippet} 
              onBack={() => setView('list')} 
              onEdit={(s) => {
                setSelectedSnippet(s);
                setView('edit');
              }}
              onDelete={handleDeleteSnippet}
            />
          )}

          {(view === 'add' || view === 'edit') && (
            <SnippetForm 
              onSave={handleSaveSnippet} 
              onCancel={() => setView(view === 'edit' ? 'detail' : 'list')}
              initialData={view === 'edit' ? selectedSnippet : null}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default App;